import { useEffect, useRef, useState } from 'react';
import { endpoints } from '../../../api';
import { studioPrefill } from '../../studio/studio-prefill';
import type { CircleFormState } from '../../studio/circle/CircleFieldInput';

/** Черновик отделён от неизменяемого intent: редактирование никогда не меняет неизвестный POST. */
export function useTextDraft(state: CircleFormState, seeded: boolean) {
  const { ownerId, form, intent, inputs, setInputs, setUploadIds } = state;
  const identity = ownerId && form ? `${ownerId}:${form.app_id}` : '';
  const ready = useRef('');
  const [badKey, setBadKey] = useState('');
  const [settled, setSettled] = useState('');
  useEffect(() => {
    if (!identity || !form) return;
    if (intent) {
      ready.current = identity;
      let active = true;
      void Promise.resolve().then(() => {
        if (active) setSettled(identity);
      });
      return () => {
        active = false;
      };
    }
    if (ready.current === identity) return;
    const controller = new AbortController();
    ready.current = '';
    const restore = async () => {
      try {
        const raw = sessionStorage.getItem(`steos:text:draft:${identity}`);
        if (raw && !seeded) {
          const saved = JSON.parse(raw) as { owner: number; app: number; inputs: Record<string, string> };
          if (
            saved.owner !== ownerId ||
            saved.app !== form.app_id ||
            !saved.inputs ||
            Array.isArray(saved.inputs) ||
            Object.entries(saved.inputs).some(([k, v]) => !/^[a-z][a-z0-9_]{0,63}$/.test(k) || typeof v !== 'string' || v.length > 20000)
          )
            throw Error('invalid');
          const next = await studioPrefill(form, saved.inputs, null, controller.signal);
          if ((await endpoints.me()).user.id !== ownerId) throw Error('owner_changed');
          if (controller.signal.aborted) return;
          setInputs(next.inputs);
          setUploadIds(next.uploadIds);
        }
        if (!controller.signal.aborted) ready.current = identity;
      } catch {
        if (!controller.signal.aborted) setBadKey(identity);
      } finally {
        if (!controller.signal.aborted) setSettled(identity);
      }
    };
    void restore();
    return () => controller.abort();
  }, [identity, form, intent, seeded, ownerId, setInputs, setUploadIds]);
  useEffect(() => {
    if (!identity || intent || ready.current !== identity) return;
    const timer = setTimeout(() => {
      try {
        const safe = Object.fromEntries(Object.entries(inputs).filter(([key]) => key !== 'consent_accepted'));
        sessionStorage.setItem(`steos:text:draft:${identity}`, JSON.stringify({ owner: ownerId, app: form!.app_id, inputs: safe }));
      } catch {
        setBadKey(identity);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [identity, inputs, intent, ownerId, form]);
  async function reset() {
    try {
      if ((await endpoints.me()).user.id !== ownerId) return;
      sessionStorage.removeItem(`steos:text:draft:${identity}`);
      ready.current = identity;
      setBadKey('');
    } catch {
      setBadKey(identity);
    }
  }
  return { blocked: badKey === identity && !!identity, restoring: !intent && !!identity && settled !== identity, reset };
}
